import { List } from 'immutable';

/**
 * Extract relay info (voltage, loads, etc.) from an Immutable relay Map.
 */
export function extractRelayInfo(relay) {
  const voltage = relay.get('voltage', 120);
  const loads = relay.get('relay_loads', List());
  return {
    id: relay.get('id'),
    voltage,
    powerMeasurementActive: relay.get('power_measurement_active', true),
    loads: loads
      .map(load => ({
        id: load.get('id'),
        name: load.get('name', ''),
        fixtureId: load.getIn(['fixture', 'id']),
        fixtureType: load.getIn(['fixture', 'name'], 'Unknown'),
        fixtureWatts: load.getIn(['fixture', 'watts'], 0),
        fixtureCount: load.get('number_of_fixtures', 0),
      }))
      .toJS(),
  };
}

/**
 * Build relay pairing data from an Immutable List of relays and total relay count.
 *
 * Returns:
 *   relayPairMap: { primaryRelayNumber: secondaryRelayNumber }
 *   pairedSecondaries: Set of secondary relay numbers (to skip in display)
 *   relayInfo: { relayNumber: { id, voltage, powerMeasurementActive, loads } }
 *   displayRelays: [{ key, label, relayNumbers }]
 *   expectedReadings: { [dataKey]: totalExpectedWatts }
 */
export function buildRelayDisplayData(relays, numberOfRelays, t) {
  const relayLabel = t ? t('measurements.relay') : 'Relay';
  const relayPairMap = {};
  const pairedSecondaries = new Set();
  const relayInfo = {};

  relays.forEach(relay => {
    const relayNum = relay.get('relay_number');
    const secondaryRelay = relay.get('secondary_relay');
    if (secondaryRelay) {
      const secondaryNum = secondaryRelay.get('relay_number');
      relayPairMap[relayNum] = secondaryNum;
      pairedSecondaries.add(secondaryNum);
      relayInfo[secondaryNum] = extractRelayInfo(secondaryRelay);
    }
    relayInfo[relayNum] = extractRelayInfo(relay);
  });

  // Build display relay list (paired relays combined, secondaries removed)
  const displayRelays = [];
  for (let i = 1; i <= numberOfRelays; i++) {
    if (pairedSecondaries.has(i)) continue;
    if (relayPairMap[i]) {
      displayRelays.push({
        key: `relay_${i}_${relayPairMap[i]}_power`,
        label: `${relayLabel} ${i}+${relayPairMap[i]}`,
        relayNumbers: [i, relayPairMap[i]],
      });
    } else {
      displayRelays.push({
        key: `relay_${i}_power`,
        label: `${relayLabel} ${i}`,
        relayNumbers: [i],
      });
    }
  }

  // Expected readings per display relay — use primary relay's loads only
  // (voltage represents the combined circuit, so primary value is the full reading)
  const expectedReadings = {};
  displayRelays.forEach(dr => {
    const primaryRn = dr.relayNumbers[0];
    const loads = relayInfo[primaryRn]?.loads || [];
    const totalExpected = loads.reduce((s, l) => s + l.fixtureWatts * l.fixtureCount, 0);
    if (totalExpected > 0) {
      expectedReadings[dr.key] = totalExpected;
    }
  });

  return { relayPairMap, pairedSecondaries, relayInfo, displayRelays, expectedReadings };
}
